import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { Endpoint, FULL_ROUTES, getFilterDates, isContrato, Report, ReportService, StatusBeneficiario, ToastServiceStipulator } from '@philips-emr/hpms-stipulator-commons';
import { HPMSOnCreateOption, HPMSOnOptionClick } from '@philips-emr/hpms-stipulator-components/lib/events/event';
import { HpmsCustomCardEvent, HpmsCustomCardMenuRenderEvent } from '@philips-emr/hpms-stipulator-components/lib/types';
import { FetchAPI, FilterAPI, Stamp, StampIcon } from '@philips-emr/tws-components';
import { Color } from '@philips-emr/tws-constants';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnClear, OnClick, OnDynamicContentRender, OnMount, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { ClearEvent, RenderEvent, SubmitEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, ModalService } from '@philips-emr/tws-core/lib/services';
import { applicationContext } from '@philips-emr/tws-foundation';
import { noop } from '@philips-emr/tws-utils';

import BaseController from '../BaseController';

interface Data {
  nrSeqInclusao: number;
  dsStatus: string;
  ieStatus: StatusBeneficiario;
  nmPessoaFisica: string;
}

const OptionKey = {
  DETALHES: 'detalhes',
  LIBERAR: 'liberar',
  CANCELAR: 'cancelar',
  IMPRIMIR: 'imprimir',
  DESFAZER: 'desfazer',
  REENVIAR: 'reenviar',
  INCONSISTENCIAS: 'inconsistencias',
};

@Controller({ fragment: 'inclusao-beneficiario-consulta' })
export default class InclusaoBeneficiarioConsultaController extends BaseController {

  @Inject('fetch-consulta')
  private fetchConsulta: FetchAPI;

  private reportService: ReportService;

  private exibir: boolean;

  @OnMount('panel-inclusao-beneficiario')
  onMount(): void {
    applicationContext.openModule('inclusao-beneficiario');
  }

  @OnClear('filter-consulta')
  onClearFilter(event: ClearEvent): void {
    const api = event.target as unknown as FilterAPI;

    api.setData({});
    api.setData(getFilterDates('dtInicio', 'dtFim', 3));
  }

  @OnSubmit('filter-consulta')
  onSubmitFilter(event: SubmitEvent): void {
    const { record, target } = event;
    const api = target as unknown as FilterAPI;

    const domain = isContrato() ? 2697 : 4968;
    this.context.set({ domain });

    const nrSeqSubestipulante = this.context.get('nrSeqSubestipulante');
    nrSeqSubestipulante && this.fetchConsulta.setParameters({ ...this.fetchConsulta.getParameters(), nrSeqSubestipulante });

    if (record && record.dtInicio && record.dtFim) {
      return;
    }

    const { dtInicio, dtFim } = getFilterDates('dtInicio', 'dtFim', 3);
    !record.dtInicio && (record.dtInicio = dtInicio);
    !record.dtFim && (record.dtFim = dtFim);

    api.setData({});
    api.setData({ ...api.getRecord(), dtInicio: record.dtInicio, dtFim: record.dtFim });

    void this.exibirImprimir().then((resp: boolean) => this.exibir = resp);
  }

  @OnClick(['action-nova-inclusao'])
  onaAtionNovaInclusaoClick(): void {
    this.historyService.push(FULL_ROUTES.BENEF_NOVA_INCLUSAO);
  }

  @OnDynamicContentRender('hpms-custom-card')
  onDynamicContentRender(event: RenderEvent): void {
    const { data, render } = event;
    const { ieStatus, dsStatus } = data as Data;

    render((): ReactElement => <Stamp icon={this.getIconBySituacao(ieStatus)} color={this.getColorBySituacao(ieStatus)} label={dsStatus} />);
  }

  @HPMSOnCreateOption('hpms-custom-card')
  onCreateOption(event: HpmsCustomCardMenuRenderEvent<Record<string, Value>>): void {
    const { data, key, shouldRender } = event;
    const { ieStatus } = data as unknown as Data;
    const status = ![StatusBeneficiario.RO, StatusBeneficiario.R, StatusBeneficiario.AL, StatusBeneficiario.I].includes(ieStatus);
    const imprimir = this.exibir && status;
    const show = {
      [OptionKey.DETALHES]: true,
      [OptionKey.LIBERAR]: [StatusBeneficiario.AL].includes(ieStatus),
      [OptionKey.CANCELAR]: [StatusBeneficiario.AL, StatusBeneficiario.I].includes(ieStatus),
      [OptionKey.IMPRIMIR]: imprimir,
      [OptionKey.DESFAZER]: [StatusBeneficiario.P].includes(ieStatus),
      [OptionKey.REENVIAR]: [StatusBeneficiario.RO].includes(ieStatus),
      [OptionKey.INCONSISTENCIAS]: [StatusBeneficiario.I].includes(ieStatus),
    };

    shouldRender(show[key]);
  }

  @HPMSOnOptionClick('hpms-custom-card')
  onOptionClick(event: HpmsCustomCardEvent<Record<string, Value>>): void {
    const { data, key } = event;
    const { nrSeqInclusao, nmPessoaFisica } = data as unknown as Data;

    const action = {
      [OptionKey.DETALHES]: () => this.detalhes(nrSeqInclusao),
      [OptionKey.LIBERAR]: () => this.liberar(nrSeqInclusao),
      [OptionKey.CANCELAR]: () => this.cancelar(nrSeqInclusao),
      [OptionKey.DESFAZER]: () => this.desfazer(nrSeqInclusao),
      [OptionKey.REENVIAR]: () => this.reenviar(nrSeqInclusao),
      [OptionKey.INCONSISTENCIAS]: () => this.inconsistencias(nrSeqInclusao, nmPessoaFisica),
      [OptionKey.IMPRIMIR]: () => this.imprimir(nrSeqInclusao),
    };

    if (action[key]) {
      action[key]();
    }
  }

  private getColorBySituacao(ieStatus: StatusBeneficiario): Color {
    const color = {
      [StatusBeneficiario.A]: Color.SAFETY_GREEN,
      [StatusBeneficiario.AL]: Color.SAFETY_YELLOW,
      [StatusBeneficiario.B]: Color.SAFETY_GREEN,
      [StatusBeneficiario.C]: Color.SAFETY_BLUE,
      [StatusBeneficiario.I]: Color.SAFETY_ORANGE,
      [StatusBeneficiario.P]: Color.SAFETY_NEUTRAL,
      [StatusBeneficiario.R]: Color.SAFETY_NEUTRAL,
      [StatusBeneficiario.RO]: Color.SAFETY_RED,
      [StatusBeneficiario.PENDENTE]: Color.SAFETY_NEUTRAL,
      [StatusBeneficiario.CONFIRMADA]: Color.SAFETY_GREEN,
      [StatusBeneficiario.REJEITADA]: Color.SAFETY_RED,
    };

    return color[ieStatus];
  }

  private getIconBySituacao(ieStatus: StatusBeneficiario): StampIcon {
    const icon = {
      [StatusBeneficiario.A]: StampIcon.CHECK,
      [StatusBeneficiario.AL]: StampIcon.WARNING,
      [StatusBeneficiario.B]: StampIcon.CLOCK,
      [StatusBeneficiario.C]: StampIcon.CHECK,
      [StatusBeneficiario.I]: StampIcon.WARNING,
      [StatusBeneficiario.P]: StampIcon.CLOCK,
      [StatusBeneficiario.R]: StampIcon.CANCELED_OUTLINE,
      [StatusBeneficiario.RO]: StampIcon.CANCELED,
      [StatusBeneficiario.PENDENTE]: StampIcon.CLOCK,
      [StatusBeneficiario.CONFIRMADA]: StampIcon.CHECK,
      [StatusBeneficiario.REJEITADA]: StampIcon.CANCELED,
    };

    return icon[ieStatus];
  }

  private refreshList(): void {
    this.fetchConsulta.forceFetch();
  }

  private onCatch(message: string): void {
    ToastServiceStipulator.warning(message);
  }

  private liberar(nrSeqInclusao: number): void {
    const { nrSeqContrato } = this.historyService.getCurrentRouteParams();
    const params = { nrSeqInclusao, nrSeqContrato };

    HttpService.post(Endpoint.POST.INC_BENEF_LIBERAR, params).then(() => this.refreshList()).catch(response => this.onCatch(response.body));
  }

  private cancelar(nrSeqInclusao: number): void {
    const { nrSeqContrato } = this.historyService.getCurrentRouteParams();

    ModalService.openDialog({
      closeable: true,
      onClose: () => this.refreshList(),
      onSubmit: noop,
      fragment: 'inclusao-beneficiario-cancelar',
      title: '',
      contextData: { nrSeqContrato, nrSeqInclusao },
    });
  }

  private desfazer(nrSeqInclusao: number): void {
    const { nrSeqContrato } = this.historyService.getCurrentRouteParams();
    const params = { nrSeqInclusao, nrSeqContrato };

    HttpService.post(Endpoint.POST.INC_BENEF_DESFAZER, params).then(() => this.refreshList()).catch(response => this.onCatch(response.body));
  }

  private reenviar(nrSeqInclusao: number): void {
    this.historyService.push(FULL_ROUTES.BENEF_SEQ_REENVIAR_INCLUSAO, { nrSeqInclusao });
  }

  private detalhes(nrSeqInclusao: number): void {
    this.historyService.push(FULL_ROUTES.BENEF_SEQ_DETALHES, { nrSeqInclusao });
  }

  private inconsistencias(nrSeqInclusao: number, nmPessoaFisica: string): void {
    const { nrSeqContrato } = this.historyService.getCurrentRouteParams();

    ModalService.openDefault({
      title: 'stipulator.inconsistencies',
      fragment: 'inconsistencies-beneficiario',
      contextData: { nmPessoaFisica, nrSeqContrato, nrSeqInclusao, endpoint: Endpoint.GET.INCONSISTENCIAS_NOVA_INCLUSAO },
      closeable: true,
    });
  }

  private exibirImprimir = async (): Promise<boolean> => {
    const service = new ReportService('16', Endpoint.GET.RELATORIOS_POR_GRUPO);
    const relatorios = await service.getRelatoriosByCdGrupo() as unknown as Report[];
    const exibe = relatorios.length === 0 ? false : true;
    return exibe;
  };

  private imprimir(nrSeqInclusao: number): void {
    this.reportService = new ReportService('16', Endpoint.GET.RELATORIOS_POR_GRUPO);
    this.reportService.abreListaRelatorios({ NR_SEQ_INCLUSAO: nrSeqInclusao });
  }
}
